import { useState } from 'react';
import { useAsyncTaskHandler } from 'src/common/utils/useAsyncTaskHandler';

import { LONG_SEARCH_DELAY } from 'src/utilities/useSearchHooks';
import { delay } from 'src/utilities/delay';
import { deactivateDiner } from 'src/common/features/DinersManagement/functions/deactivateDiner';

export interface UseDeactivateEmployeeProps {
  corpId: string;
  employeeId: string;
  onSuccess?: () => Promise<void>;
  onError?: () => Promise<void>;
}

export interface UseDeactivateEmployeeReturn {
  deactivateEmployee: () => Promise<void>;
  isLoading: boolean;
}

export const useDeactivateEmployee = ({
  corpId,
  employeeId,
  onSuccess,
  onError,
}: UseDeactivateEmployeeProps): UseDeactivateEmployeeReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { startAsyncTask } = useAsyncTaskHandler({
    onSuccess: handleSuccess,
    onError: handleError,
  });

  async function deactivateEmployee(): Promise<void> {
    setIsLoading(true);

    await startAsyncTask(() => {
      return deactivateDiner(corpId, employeeId);
    });
  }

  async function handleSuccess(): Promise<void> {
    /**
     * The reason for this timeout is that elastic search needs some time to rearrange the indexes
     * after a record is deleted. Without this timeout the search could potentially
     * return outdated results
     */
    await delay(LONG_SEARCH_DELAY);
    await onSuccess?.();
  }

  async function handleError(): Promise<void> {
    await onError?.();
    setIsLoading(false);
  }

  return {
    deactivateEmployee,
    isLoading,
  };
};
